import {describe, expect, it, vi} from "vitest";
import {superjson} from "../common/coding.js";
import {MessageChannel} from "./channel.js";
import type {AppendOnlyLog} from "./log.js";
import type {FsDuplexPayloads} from "./protocol.js";

/**
 * A more accurate mock of AppendOnlyLog.
 * It stores and returns serialized strings, just like the real one.
 */
class InMemoryAppendOnlyLog implements AppendOnlyLog {
  private lines: string[] = [];
  private readIndex = 0;

  start = vi.fn().mockResolvedValue(undefined);
  stop = vi.fn().mockResolvedValue(undefined);

  append(line: string): Promise<void> {
    this.lines.push(line);
    return Promise.resolve();
  }

  readNewLines(): Promise<string[]> {
    const newLines = this.lines.slice(this.readIndex);
    this.readIndex = this.lines.length;
    return Promise.resolve(newLines);
  }
}

describe("MessageChannel (Stateful Manager)", () => {
  it("should complete a full handshake and manage state", async () => {
    const sharedLog = new InMemoryAppendOnlyLog();
    const initiator = new MessageChannel("initiator", superjson, sharedLog);
    const handler = new MessageChannel("handler", superjson, sharedLog);

    const initiatorOpen = new Promise<void>((res) => initiator.on("open", res));
    const handlerOpen = new Promise<void>((res) => handler.on("open", res));

    initiator.send("init", {task: {id: 1}});

    await handler.handleLogData();
    await initiator.handleLogData();

    await Promise.all([initiatorOpen, handlerOpen]);

    expect(initiator.currentState).toBe("open");
    expect(handler.currentState).toBe("open");
    expect((initiator as any).localAck).toBe(1);
    expect((handler as any).localAck).toBe(1);
  });

  it("should send and receive a message after handshake", async () => {
    const sharedLog = new InMemoryAppendOnlyLog();
    const initiator = new MessageChannel("initiator", superjson, sharedLog);
    const handler = new MessageChannel("handler", superjson, sharedLog);

    initiator.send("init", {task: {}});
    await handler.handleLogData();
    await initiator.handleLogData();

    const messageReceived = new Promise<FsDuplexPayloads["chunk"]>((res) => handler.on("chunk", res));

    const chunkPayload = {data: new Uint8Array([1]) as any};
    initiator.send("chunk", chunkPayload);

    await handler.handleLogData();

    const received = await messageReceived;
    expect(received).toEqual(chunkPayload);
    expect((handler as any).localAck).toBe(2);
  });

  it("should complete a graceful shutdown handshake", async () => {
    const sharedLog = new InMemoryAppendOnlyLog();
    const initiator = new MessageChannel("initiator", superjson, sharedLog);
    const handler = new MessageChannel("handler", superjson, sharedLog);

    const initiatorClosed = new Promise<void>((res) => initiator.on("close", res));
    const handlerClosed = new Promise<void>((res) => handler.on("close", res));

    initiator.send("init", {task: {}});
    await handler.handleLogData();
    await initiator.handleLogData();

    initiator.close();

    await handler.handleLogData();
    await initiator.handleLogData();

    await Promise.all([initiatorClosed, handlerClosed]);

    expect(initiator.currentState).toBe("closed");
    expect(handler.currentState).toBe("closed");
  });
});
